import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { InputField } from '../../../components/InputController';
import { useStyles } from '../../../style/Pages/BasicCompanyInfo';
import { schemaValidationCompanyBankAccountInfo, schemaValidationCompanyBankAccount } from '../../../utils/validation';
import { selectOnboardingApplicationState, updateOnboardingDto } from '../sagas/pmonboardingSlice';
import { openAccordions } from '../ApplicationDashboard';
import { CustomDropZone } from '../components/CustomDropZone';

export const useStyle = makeStyles(() => ({
  accordionStyle: {
    borderRadius: '24px !important',
    overflow: 'hidden',
  },
}));

const textStyle = {
  fontWeight: 'bold',
};

interface FormCompanyBankAccountPropType {
  openAccordion?: string | boolean;
  setOpenAccordion?: React.Dispatch<React.SetStateAction<openAccordions | false>>;
}

export default function FormCompanyBankAccount(props: FormCompanyBankAccountPropType) {
  const selectOnboardingState = useSelector(selectOnboardingApplicationState);

  const dispatch = useDispatch();
  //Company Bank Account Saved Values
  const [savedValues, setSavedValues] = React.useState<schemaValidationCompanyBankAccountInfo>({
    bank_name: '',
    routing_number: '',
    account_number: '',
    bank_files: [],
  });

  React.useEffect(() => {
    selectOnboardingState.onboarding?.BankAccount?.BankName &&
      setSavedValues({
        ...savedValues,
        bank_name: selectOnboardingState.onboarding?.BankAccount?.BankName,
        routing_number: selectOnboardingState.onboarding?.BankAccount?.RoutingNumber,
        account_number: selectOnboardingState.onboarding?.BankAccount?.AccountNumber,
        bank_files: selectOnboardingState.onboarding?.BankAccount?.BankFiles,
      });
  }, [selectOnboardingState.onboarding]);

  const formik = useFormik<schemaValidationCompanyBankAccountInfo>({
    initialValues: savedValues,
    validationSchema: schemaValidationCompanyBankAccount,
    onSubmit: async (values) => {
      dispatch({
        type: updateOnboardingDto.type,
        payload: {
          BankAccount: {
            EntityUuid: selectOnboardingState.onboarding?.BankAccount?.EntityUuid,
            BankName: values.bank_name,
            RoutingNumber: values.routing_number,
            AccountNumber: values.account_number,
            BankFiles: values.bank_files,
          },
        },
      });
    },
    enableReinitialize: true,
  });

  const handleFiles = (acceptedFiles: File[]) => {
    let files = formik.values.bank_files || [];

    if (files.length + acceptedFiles.length <= 3) {
      acceptedFiles.forEach((af: File) => {
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      formik.setFieldValue('bank_files', files);
    }
  };

  const handleDelete = (file: File) => {
    const files = formik.values.bank_files || [];
    formik.setFieldValue(
      'bank_files',
      files.filter((f) => f.name !== file.name),
    );
  };

  const classes = useStyle();
  const classB = useStyles();
  return (
    <Box sx={{ marginTop: '3%' }}>
      <form onSubmit={formik.handleSubmit}>
        <Accordion
          className={classes.accordionStyle}
          expanded={props.openAccordion === openAccordions.companyBankAccount}
          onChange={(e: React.SyntheticEvent, isExpanded: boolean) => {
            props.setOpenAccordion && props.setOpenAccordion(isExpanded ? openAccordions.companyBankAccount : false);
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography sx={textStyle}>Company Bank Account</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              style={{
                fontSize: '18px',
                lineHeight: '22px',
                fontFamily: 'Work Sans',
                color: '#6B7280',
                margin: '0px 18px 16px',
              }}
            >
              note that additional escrow and rental account are added later via the integration or manual set up. This bank account should be in your companies name.
            </Typography>

            <InputField
              name={'bank_name'}
              placeholder={'Bank Name'}
              label={'Bank Name'}
              isError={false}
              errors={formik.errors?.bank_name}
              touched={formik.touched?.bank_name}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('bank_name', e.target.value)}
              value={formik.values.bank_name || ''}
            />
            <InputField
              name={'routing_number'}
              placeholder={'Routing Number'}
              label={'Routing Number'}
              isError={false}
              errors={formik.errors?.routing_number}
              touched={formik.touched?.routing_number}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('routing_number', e.target.value)}
              value={formik.values.routing_number || ''}
            />
            <InputField
              name={'account_number'}
              placeholder={'Account Number'}
              label={'Account Number'}
              isError={false}
              errors={formik.errors?.account_number}
              touched={formik.touched?.account_number}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('account_number', e.target.value)}
              value={formik.values.account_number || ''}
            />

            <Box sx={{ margin: '10px 0 16px' }}>
              {/* maxFileSize must be in MB, example: maxFileSize={20} */}
              <CustomDropZone
                files={formik?.values?.bank_files || []}
                fileLimit={3}
                handleFilesAdded={handleFiles}
                handleFileDeleted={handleDelete}
                maxFileSize={20}
                description="Up to 20MB per file. Up to 3 Files at a time."
              />
              <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop: '10px' }}>{'Supported File Types: .jpg, .pdf , .png'}</Typography>
            </Box>

            <Box display="flex" justifyContent="end">
              <Button type="submit" disabled={Boolean(!formik.isValid || !formik.dirty)} className={classB.signUpBtn}>
                <Typography className={classB.signUpBtnText}>update</Typography>
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </form>
    </Box>
  );
}
